import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FormControl,
  FormLabel,
  Grid2 as Grid,
  Typography,
} from "@mui/material";

import { RootState } from "@app/store";
import {
  getAllVMs,
  fetchVMs,
  selectVMStatus,
} from "@features/environments/environmentsSlice";
import { getSelectedCustomerId } from "@features/customers/customerSlice";
import { useAppDispatch } from "@features/common/StoreHook";
import { IVirtualMachine } from "@features/environments/types";
import { formLabelStyle, formControlStyle } from "@features/common/styles";
import EditButtonGroup from "@features/common/ButtonGroups/EditButtonGroup";
import { permissionsApi } from "@features/authorization/authService";
import { checkPermissions } from "@features/authorization/types";
import { ITask } from "../types";
import { getSelectedTask, updateTask, selectTasksStatus } from "../tasksSlice";
import { LeftRightSelector } from "./LeftRightSelector";
import InfoBox from "@features/common/InfoBox";

// leftGlobal is the "upper" level components state, i.e. total number of virtual machines in left or right side
// passed down to this component to manage state when state is submitted to API

export const Environments = () => {
  // General
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const customerId = useSelector(getSelectedCustomerId);
  const taskToUpdate: ITask | undefined = useSelector((state: RootState) =>
    getSelectedTask(state),
  );

  // State
  const [showVmDisclaimer, setShowVmDisclaimer] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isSubmitting = useSelector(selectTasksStatus) === "submitting";
  const vmListLoading = useSelector(selectVMStatus) === "loading";

  // VMs
  const allVms = useSelector((state: RootState) => getAllVMs(state));
  const [cloudFilteredVms, setCloudFilteredVms] = useState<IVirtualMachine[]>(
    [],
  );
  const [leftListFull, setLeftListFull] = useState<string[]>([]);

  // Permissions
  const { data } = permissionsApi.useGetCustomerPermissionsQuery(customerId);
  const taskPermissions = checkPermissions("tasks", data);

  useEffect(() => {
    const filtered: IVirtualMachine[] = [];
    for (const vm of allVms) {
      if (
        (vm.cloud_platform == "Azure" && taskToUpdate?.cloud_platforms.azure) ||
        (vm.cloud_platform == "GCP" && taskToUpdate?.cloud_platforms.gcp)
      ) {
        filtered.push(vm);
      }
    }
    if (filtered.length !== allVms.length) {
      setShowVmDisclaimer(true);
    }
    setCloudFilteredVms(filtered);
  }, [allVms]);

  useEffect(() => {
    const vmNamesLeft: string[] = [];
    if (cloudFilteredVms && taskToUpdate) {
      for (const vm of cloudFilteredVms) {
        if (taskToUpdate.vm_names?.includes(vm.name)) {
          vmNamesLeft.push(vm.name);
        }
      }
    }
    setLeftListFull(vmNamesLeft);
  }, [cloudFilteredVms]);

  useEffect(() => {
    dispatch(fetchVMs(customerId));
  }, []);

  useEffect(() => {
    if (taskToUpdate) {
      setSubmitDisabled(false);
    } else setSubmitDisabled(true);
  }, [taskToUpdate]);

  const handleSubmitSelected = () => {
    if (taskPermissions.update) {
      if (taskToUpdate) {
        const taskToUpdateCopy: ITask = {
          ...taskToUpdate,
          vm_names: leftListFull,
        };

        dispatch(updateTask(taskToUpdateCopy))
          .unwrap()
          .then(() => {
            setIsEditing(false);
            toast.success("Task environments updated successfully");
          })
          .catch(() => {
            toast.error("Failed to update task environments");
          });
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    const vmNamesLeft: string[] = [];
    if (cloudFilteredVms && taskToUpdate) {
      for (const vm of cloudFilteredVms) {
        if (taskToUpdate.vm_names.includes(vm.name)) {
          vmNamesLeft.push(vm.name);
        }
      }
    }
    setLeftListFull(vmNamesLeft);
  };

  return (
    <FormControl component="fieldset" style={formControlStyle}>
      <FormLabel component="legend" sx={formLabelStyle}>
        {t("title-environments")}
      </FormLabel>
      <Grid container spacing={{ xs: 3, sm: 0 }}>
        <Grid size={{ xs: 12 }}>
          <Grid
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            paddingBottom={
              showVmDisclaimer && !vmListLoading ? "15px" : undefined
            }
          >
            <Typography variant={"h6"}>Production</Typography>
            {showVmDisclaimer && !vmListLoading && (
              <InfoBox
                variant="filled"
                severity="info"
                text={t("prompt-not-seeing-vm")}
                additionalText={t("description-not-seeing-vm")}
              />
            )}
          </Grid>
          <LeftRightSelector
            vmCategory="Production"
            virtualmachines={cloudFilteredVms}
            setleftGlobal={setLeftListFull}
            isEditing={isEditing}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant={"h6"}>Development</Typography>
          <LeftRightSelector
            vmCategory="Development"
            virtualmachines={cloudFilteredVms}
            setleftGlobal={setLeftListFull}
            isEditing={isEditing}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant={"h6"}>Onsite</Typography>
          <LeftRightSelector
            vmCategory="Onsite"
            virtualmachines={cloudFilteredVms}
            setleftGlobal={setLeftListFull}
            isEditing={isEditing}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <EditButtonGroup
            disabled={submitDisabled || isSubmitting}
            isEditing={isEditing}
            handleEdit={() => setIsEditing(!isEditing)}
            handleSave={handleSubmitSelected}
            handleCancel={handleCancel}
          />
        </Grid>
      </Grid>
    </FormControl>
  );
};
